// Native & external imports
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";
import { Injectable, inject } from "@angular/core";
import {
  AbstractControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { ApiCallsService } from "icamppro-shared-components";
import { environment } from "src/environments/environment";
// Services
import { AppRouterService } from "src/routing/routes-service";
import { LayoutService } from "./layout.service";
// Objects
import { HttpResponseObject } from "@iclassproinc/icamppro-shared-components";
import {
  CmpCommunicationSettingObject,
  CmpCommunicationSmsSettingObject,
  CmpSmsOptInCopy,
} from "src/objects/cmp-user.object";
// Constants
import { CmpAppSettings } from "./cmp-app-settings.service";
import { GtmService } from '@icamppro-customer-portal/shared/cmp-base';

@Injectable({
  providedIn: "root",
})
export class HelperService {
  private routerService = inject(AppRouterService);
  private apiService = inject(ApiCallsService);
  private layoutService = inject(LayoutService);
  private appSettings = inject(CmpAppSettings);
  private gtmService = inject(GtmService);

  public isIframe = false;
  public formValue: any = {};
  public redirectUrl = "";
  public classNames: { [index: string]: boolean };
  public originalLocation = "";
  private _tried = false;
  public communicationEmailSettingObject = new CmpCommunicationSettingObject();
  public communicationSmsSettingObject = new CmpCommunicationSmsSettingObject();
  private smsOptInCopySubject: BehaviorSubject<CmpSmsOptInCopy> =
    new BehaviorSubject<CmpSmsOptInCopy>(new CmpSmsOptInCopy());
  public smsOptInCopy$: Observable<CmpSmsOptInCopy> =
    this.smsOptInCopySubject.asObservable();
  public staffSettingsObject = new CmpCommunicationSmsSettingObject();
  public saveUtmFields = false;
  public enableSms = false;
  private settingsLoaded = false;

  constructor() {
    this.classNames = {
      firstName: false,
      email: false,
      message: false,
    };
  }

  /**
   * @method changeFormField
   * @param form UntypedFormGroup
   * @param fieldName string
   * @param setValue boolean
   * @param value any
   * @param setValidators boolean
   * @param validators Array<ValidatorFn>
   * @param disable boolean
   * @description This method will change the form control values and validators
   */
  public changeFormField(
    form: UntypedFormGroup,
    fieldName: string,
    setValue: boolean,
    value: any = "",
    setValidators: boolean = false,
    validators: Array<ValidatorFn> = [],
    disable = false,
  ) {
    const fieldControl = form.controls[fieldName];
    if (setValue) {
      fieldControl.setValue(value);
    }
    if (setValidators) {
      fieldControl.setValidators(validators);
    }
    if (setValue || setValidators) {
      fieldControl.updateValueAndValidity();
    }
    if (disable) {
      fieldControl.disable();
    } else {
      fieldControl.enable();
    }
  }

  /**
   * @method validateFormGroup
   * @param form: UntypedFormGroup
   * @description this methods validates all the form controls
   */
  public validateFormGroup(
    form: UntypedFormGroup,
    skipFields: Array<string> = [],
  ) {
    let result = true;
    for (const field in form.controls) {
      if (form.controls[field] && skipFields.indexOf(field) === -1) {
        if (!form.controls[field].valid) {
          result = false;
          form.controls[field].markAsDirty();
        }
      }
    }
    return result;
  }

  /**
   * @method pushDataLayer
   * @param screenSlug - The screen slug identifier
   * @param screenTitle - The screen title
   * @description pushes data layer variable
   */
  public pushDataLayer(screenSlug: string, screenTitle: string): void {
    if (!this.originalLocation && !this._tried && this.layoutService.loggedIn) {
      this.gtmService.storeScreenContext(screenSlug, screenTitle);
      this.getMostRecentUrl(true);
    } else {
      this.gtmService.pushScreenView(screenSlug, screenTitle);
    }
  }

  /**
   * @method pushGTMEvent
   * @param event - The GTM event name
   * @param includeAccount - Whether to include account info
   * @param gender - Optional gender value
   * @description To push GTM events
   */
  public pushGTMEvent(event: string, includeAccount = false, gender = ''): void {
    const companyName = includeAccount ? this.appSettings.getAppSetting('companyName') : undefined;
    this.gtmService.pushGTMEvent(event, {
      includeAccount,
      companyName: companyName || '',
      gender: gender || undefined,
    });
  }

  /**
   * @method resetGtm
   * @description resets the data layer variable
   */
  public resetGtm(): void {
    this.gtmService.resetGtm();
  }

  /**
   * @method setUrl
   * @param url string
   * @description sets the url based on the string given
   */
  public setUrl(url: string) {
    if (url) {
      url = url.replace("?", "");
      const cookieName = "icamppro-utm-qs";
      const cookieValue = this.constructQueryString(url);
      let expirationTime = 2592000 * 3; // three month in seconds
      expirationTime = expirationTime * 1000;
      const cookieDate = new Date();
      const dateTimeNow = cookieDate.getTime();
      cookieDate.setTime(dateTimeNow + expirationTime); // Sets expiration time (Time now + one month)
      const date = cookieDate.toUTCString(); // Converts milliseconds to UTC time string
      if (document.location) {
        const domainArr = document.location.hostname.split(".");
        const domain =
          domainArr[domainArr.length - 2] +
          "." +
          domainArr[domainArr.length - 1];
        document.cookie =
          cookieName +
          "=" +
          cookieValue +
          "; SameSite=None; Secure; expires=" +
          date +
          `; path=/; domain=${domain}`; // Sets cookie for all subdomains
      }
    }
    if (!this.originalLocation) {
      this.getUrl();
    }
  }

  /**
   * @method constructQueryString
   * @param st string
   * @returns string
   * @description constructs query string
   */
  public constructQueryString(st: string) {
    let isUtm = false;
    if (st.match("utm_")) {
      isUtm = true;
    }

    const stArr = st.split("&");
    let queryString = "";
    if (stArr.length) {
      stArr.forEach((query) => {
        if (query.match("utm_")) {
          if (queryString) {
            queryString = queryString + "&" + query;
          } else {
            queryString = queryString + query;
          }
        }
      });
    }
    return queryString;
  }

  /**
   * @method getUrl
   * @description gets the url
   */
  public getUrl() {
    const name1 = "icamppro-utm-qs" + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name1) === 0) {
        const utm = c.substring(name1.length, c.length);
        if (utm.match("utm_")) {
          this.makeUrl(utm);
          return utm;
        } else {
          this.makeUrl(utm);
          return utm;
        }
      }
    }
    return "";
  }

  /**
   * @method makeUrl
   * @param utm string
   * @returns string
   * @description constructs url
   */
  private makeUrl(utm: string) {
    if (utm && document.location) {
      const url = `${document.location.protocol}//${document.location.hostname}/?${utm}`;
      this.originalLocation = url;
      this.setUrl(utm);
      return utm;
    }
  }

  /**
   * @method getMostRecentUrl
   * @param onLoad boolean
   * @description most recent url
   */
  public getMostRecentUrl(onLoad?: true) {
    if (this.layoutService.familyId > 0) {
      const url = this.routerService.customizeUrl(
        this.routerService.backendApiUrl.campaignUrl,
        `familyId:${this.layoutService.familyId}`,
      );
      this.apiService.get(url).subscribe((response) => {
        if (response && response.data) {
          this._tried = true;
          if (this.originalLocation) {
            this.setMostRecentUrl();
          } else {
            this.originalLocation =
              typeof response.data === "string" ? response.data : "";
          }
          if (onLoad) {
            this.pushDataLayer(this.gtmService.screenSlug, this.gtmService.screenTitle);
          }
        }
      });
    }
  }

  /**
   * @method setMostRecentUrl
   * @description sets the recent url
   */
  public setMostRecentUrl() {
    if (this.layoutService.familyId > 0) {
      const url = this.routerService.customizeUrl(
        this.routerService.backendApiUrl.campaignUrl,
        `familyId:${this.layoutService.familyId}`,
      );
      const payLoad = {
        mostRecentCampaignUrl: this.originalLocation,
        saveUtmFields: this.saveUtmFields,
      };
      this.apiService.post(url, payLoad).subscribe((response) => {
        if (response && response.data) {
          this.originalLocation =
            typeof response.data === "string" ? response.data : "";
          this.saveUtmFields = false;
        }
      });
    }
  }

  public getCampaignUrl() {
    let campaignUrl: string | null = null;
    const cookieUrl = this.getUrl();
    campaignUrl = cookieUrl ? cookieUrl : this.originalLocation;
    return campaignUrl && typeof campaignUrl === "string"
      ? campaignUrl.substring(campaignUrl.indexOf("?") + 1)
      : null;
  }

  /*
   * @method getCommunicationSetting
   * @description return the communication email and sms settings
   */
  public getCommunicationSetting() {
    if (this.settingsLoaded) {
      return;
    }
    this.settingsLoaded = true;
    this.apiService
      .get(this.routerService.backendApiUrl.communicationSettings)
      .subscribe((response) => {
        if (!response.hasErrors) {
          this.communicationEmailSettingObject = response.data["email"];
          this.communicationSmsSettingObject = response.data["sms"];
          this.smsOptInCopySubject.next(this.buildSmsOptInCopy());
        }
      });
  }

  /**
   * @method returnEmailOptedInExplanation
   * @description return the promotion email opted in explanation
   */
  public returnEmailOptedInExplanation() {
    return this.communicationEmailSettingObject
      .promotionalEmailOptInExplanation;
  }

  /**
   * @method returnSmsOptedInPrompt
   * @description return the sms opted in prompt
   */
  public returnSmsOptedInPrompt(): string {
    return this.communicationSmsSettingObject.smsOptInPrompt;
  }

  /**
   * @method returnSmsOptedInExplanation
   * @description return the sms opted in explanation
   */
  public returnSmsOptedInExplanation(): string {
    return this.communicationSmsSettingObject.smsOptInExplanation;
  }

  /**
   * Builds the SMS opt-in copy used by the opt-in screens.
   * @returns The prompt, explanation and chosen display mode
   */
  private buildSmsOptInCopy(): CmpSmsOptInCopy {
    const explanation = this.communicationSmsSettingObject.smsOptInExplanation || '';
    const copy = new CmpSmsOptInCopy();
    copy.prompt = this.communicationSmsSettingObject.smsOptInPrompt;
    copy.explanation = explanation;
    // on-page links must open in a new tab so the form is not lost
    copy.explanationHtml = explanation
      ? explanation
          // drop any author-set target/rel first so they cannot be duplicated
          .replace(/<a(\s[^>]*?)\s*target=("[^"]*"|'[^']*')/gi, '<a$1')
          .replace(/<a(\s[^>]*?)\s*rel=("[^"]*"|'[^']*')/gi, '<a$1')
          .replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer" ')
      : '';
    copy.inline = this.communicationSmsSettingObject.smsOptInExplanationDisplay === 'inline';
    return copy;
  }

  /**
   * @method callToLoadBalancer
   * @description call load balancer url
   */
  public callToLoadBalancer(apiService: any) {
    apiService.cmpEnv.apiUrl = environment.loadBalancerApiUrl;
    return apiService;
  }

  /**
   * @method revertLoadBalancer
   * @description revert load balancer url to api url
   */
  public revertLoadBalancer(apiService: any) {
    apiService.cmpEnv.apiUrl = environment.apiUrl;
    return apiService;
  }

  /**
   * @method mustMatch
   * @description creates the ValidateFn for angular form validation
   * @returns void
   */
  public mustMatch(formControl: AbstractControl): ValidationErrors {
    const control = formControl.get("password");
    const matchingControl = formControl.get("confirmPassword");
    if (matchingControl.errors && !matchingControl.errors.mustMatch) {
      // return if another validator has already found an error on the matchingControl
      return null;
    }
    // set error on matchingControl if validation fails
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
      return { mustMatch: true };
    } else {
      matchingControl.setErrors(null);
      return null;
    }
  }

  /**
   * @method emailPattern
   * @description this method will return regex pattern for email validation
   * @returns string
   */
  public emailPattern() {
    // Local part follows RFC 5322 dot-atom rules: atoms separated by single dots,
    // with no leading, trailing, or consecutive dots (e.g. a trailing dot like "name.@example.com" is rejected).
    const emailPattern =
      /^[a-zA-Z0-9!#$%^&*_+=|{}?'`~\-\/]+(?:\.[a-zA-Z0-9!#$%^&*_+=|{}?'`~\-\/]+)*@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailPattern;
  }

  /**
   * @method loadTwilioSetting
   * @description sets the twilio Integration
   * @returns void
   *
   */
  public async loadTwilioSetting() {
    const url = this.routerService.backendApiUrl.twilioSettings;
    const response: HttpResponseObject<any> = await firstValueFrom(
      this.apiService.get(url),
    );
    if (response.data && !response.hasErrors) {
      this.enableSms = !!response.data.twilioIntegration;
    }
  }

  /**
   * @method isNumericInput
   * @description  Checks if the input is a numeric input.
   */
  public isNumericInput(event: any) {
    const key = event.keyCode;
    return (key >= 48 && key <= 57) || (key >= 96 && key <= 105);
  }

  /**
   * @method isModifierKey
   * @description Checks if the key pressed is a modifier key (keys used for modifying the input text)
   */
  public isModifierKey(event: any) {
    const key = event.keyCode;
    return (
      event.shiftKey === true ||
      key === 35 ||
      key === 36 ||
      key === 8 ||
      key === 9 ||
      key === 13 ||
      key === 46 ||
      (key > 36 && key < 41) ||
      ((event.ctrlKey === true || event.metaKey === true) &&
        (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
    );
  }

  /**
   * @method enforceFormat
   * @description Enforces the input format to numeric. If the input is not numeric and is not a modifier key, prevents the default event action.
   */
  public enforceFormat(event: any) {
    if (!this.isNumericInput(event) && !this.isModifierKey(event)) {
      event.preventDefault();
    }
  }

  /**
   * @method formatToPhone
   * @description Formats the input to a phone number format. Does not run if a modifier key is pressed. Formats the phone number as the user types.
   */
  public formatToPhone(event: any, form: UntypedFormGroup) {
    if (this.isModifierKey(event)) {
      return;
    }
    const element = event.target.id;
    const fieldControl = form?.controls[element];
    const target = event.target;
    const input = event.target.value.replace(/\D/g, "").substring(0, 10);
    const first = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);

    if (input.length > 6) {
      target.value = `(${first}) ${middle}-${last}`;
    } else if (input.length > 3) {
      target.value = `(${first}) ${middle}`;
    } else if (input.length === 3) {
      target.value = `(${first})`;
    } else if (input.length > 0) {
      target.value = `(${first}`;
    }
    if (input.length === 0) {
      fieldControl.setValue("");
    } else fieldControl.setValue(target.value);
  }

  /**
   * @method validateInput
   * @description Formats the paste input phone number.
   */
  public validateInput(event: any, form: UntypedFormGroup) {
    setTimeout(() => {
      this.formatToPhone(event, form);
    }, 0);
  }

  /**
   * @method viewPasswordToggle
   * @description Hide/View the password.
   */
  public viewPasswordToggle(viewIconBtn: string, passwordField: string) {
    const view = document.getElementById(viewIconBtn);
    const input = document.getElementById(
      passwordField,
    ) as HTMLInputElement | null;
    if (view.classList.contains("view-toggle-2-icons")) {
      if (view.dataset.showIcon === "1") {
        view.setAttribute("data-show-icon", "2");
        input.setAttribute("type", "text");
      } else if (view.dataset.showIcon === "2") {
        view.setAttribute("data-show-icon", "1");
        input.setAttribute("type", "password");
      }
    }
  }
}
